import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  IconButton,
  Stack,
  Tooltip,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ThumbUp, Edit, Delete, Add } from '@mui/icons-material';
import { GridColDef, GridFilterModel, GridOverlay } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../../utils/adminDashboardUtils';

import fetchPartnerResources, {
  deletePartnerResource,
  fetchResourceCategories,
  patchPartnerResource,
  patchPartnerResourceImages,
  postPartnerResource,
  RESOURCE_CATEGORY_TITLES,
} from '../../../services/partnerResources';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';
import BaseToolbar from '../../Datagrid/BaseToolbar';

import {
  PartnerResource,
  PartnerResourceCategory,
  ResourceImageType,
} from '../../../interfaces/partnerResources.interface';
import PartnerResourcesModal from '../../PartnerResources/PartnerResourcesModal';
import { getResourceImageUrl } from '../../../services/adminUtils';

const MemberGroupDiscounts = () => {
  const [rows, setRows] = useState<PartnerResource[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [loading, setLoading] = useState(true);
  const [memberDiscountsCategoryId, setMemberDiscountsCategoryId] = useState<number | null>(null);

  const [newKeyTermModalOpen, setNewKeyTermModalOpen] = useState(false);
  const [newTerm, setNewTerm] = useState('');
  const [newDefinition, setNewDefinition] = useState('');
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreviewUrl, setNewImagePreviewUrl] = useState<string | null>(null);
  const [addTermError, setAddTermError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PartnerResource | null>(null);
  const [editTerm, setEditTerm] = useState('');
  const [editDefinition, setEditDefinition] = useState('');
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreviewUrl, setEditImagePreviewUrl] = useState<string | null>(null);
  const [editKeyTermError, setEditKeyTermError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<PartnerResource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showSaved, setShowSaved] = useState(false);
  const [showAddPreview, setShowAddPreview] = useState(false);
  const [showPRCPreview, setShowPRCPreview] = useState(false);

  function CustomToolbar(props: { openInviteAdminModal: () => void }) {
    const { openInviteAdminModal, ...rest } = props;

    return (
      <BaseToolbar
        {...rest}
        csvOptions={{ fileName: `member_group_discounts_${moment().format('MM_DD_YYYY')}` }}
      >
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={() => setNewKeyTermModalOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Discount Member
          </Button>
        </Box>
      </BaseToolbar>
    );
  }

  const fetchMemberDiscounts = useCallback(
    async (categoryId: number) => {
      setLoading(true);
      try {
        const { count, results } = await fetchPartnerResources({
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
          ordering:
            sortModel.length > 0
              ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
              : undefined,
          filterModel: filterModelApplied,
          categoryId,
        });

        setRows(results);
        setRowCount(count);
      } catch (error) {
        console.error('Error fetching member discounts:', error);
      } finally {
        setLoading(false);
      }
    },
    [paginationModel, sortModel, filterModelApplied],
  );

  const handleFetchResourceCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { results } = await fetchResourceCategories();

      const categoryId = results.find(
        (cat: PartnerResourceCategory) => cat.title === RESOURCE_CATEGORY_TITLES.MEMBER_DISCOUNTS,
      )?.id;

      if (categoryId) {
        setMemberDiscountsCategoryId(categoryId);
        await fetchMemberDiscounts(categoryId);
      } else {
        setMemberDiscountsCategoryId(null);
        console.warn('MEMBER DISCOUNTS category not found.');
      }
    } catch (error) {
      console.error('Error fetching resource categories:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchMemberDiscounts]);

  useEffect(() => {
    handleFetchResourceCategories();
  }, [handleFetchResourceCategories]);

  useEffect(() => {
    if (memberDiscountsCategoryId) {
      fetchMemberDiscounts(memberDiscountsCategoryId);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filterModelApplied,
    memberDiscountsCategoryId,
    fetchMemberDiscounts,
  ]);

  // image previews
  useEffect(() => {
    if (!newImageFile) return setNewImagePreviewUrl(null);
    const url = URL.createObjectURL(newImageFile);
    setNewImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newImageFile]);

  useEffect(() => {
    if (!editImageFile) return setEditImagePreviewUrl(null);
    const url = URL.createObjectURL(editImageFile);
    setEditImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editImageFile]);

  const handleOpenEdit = useCallback((row: PartnerResource) => {
    setEditingRow(row);
    setEditTerm(row.title ?? '');
    setEditDefinition(row.subDescription ?? '');
    setEditImageFile(null);
    setEditKeyTermError('');
    setIsEditOpen(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    if (isSaving) return;

    setIsEditOpen(false);
    setEditingRow(null);
    setEditKeyTermError('');
    setEditTerm('');
    setEditDefinition('');
    setEditImageFile(null);
    setEditImagePreviewUrl(null);
  }, [isSaving]);

  const handleOpenDelete = useCallback((row: PartnerResource) => {
    setDeletingRow(row);
    setIsDeleteOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    if (isDeleting) return;

    setIsDeleteOpen(false);
    setDeletingRow(null);
  }, [isDeleting]);

  const handleCloseAdd = useCallback(() => {
    if (isAdding) return;

    setNewTerm('');
    setNewDefinition('');
    setNewImageFile(null);
    setNewImagePreviewUrl(null);
    setAddTermError('');
    setNewKeyTermModalOpen(false);
  }, [isAdding]);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deletingRow) return;

    setIsDeleting(true);
    try {
      const success = await deletePartnerResource(Number(deletingRow.id));
      if (!success) {
        console.error('Failed to delete member discount. Please try again.');
        return;
      }

      setRows((prev) => prev.filter((r) => r.id !== deletingRow.id));
      setRowCount((prev) => Math.max(0, prev - 1));
      handleCloseDelete();
      setShowSaved(true);
    } catch (error) {
      console.error('Error deleting member discount:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRow, handleCloseDelete]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    if (!editTerm.trim() || !editDefinition.trim()) {
      setEditKeyTermError('Both name and email are required.');
      return;
    }

    if (!editDefinition.includes('@') || editDefinition.split('@').length !== 2) {
      setEditKeyTermError('Invalid email format.');
      return;
    }

    const hasExistingImage = Boolean(getResourceImageUrl(editingRow));
    if (!editImageFile && !hasExistingImage) {
      setEditKeyTermError('Image is required. Please upload an image.');
      return;
    }

    setIsSaving(true);
    try {
      setEditKeyTermError('');

      let patchedImagesResponse: PartnerResource | null = null;
      if (editImageFile) {
        patchedImagesResponse = await patchPartnerResourceImages(Number(editingRow.id), editImageFile, null);
      }

      const updated = await patchPartnerResource(Number(editingRow.id), {
        title: editTerm,
        description: editDefinition,
        subDescription: editDefinition,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === editingRow.id
            ? {
              ...r,
              ...updated,
              images: patchedImagesResponse?.images ?? r.images,
            }
            : r,
        ),
      );

      handleCloseEdit();
      setShowSaved(true);
    } catch (error) {
      console.error('Error updating member discount:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editingRow, editTerm, editDefinition, editImageFile, handleCloseEdit]);

  const handleAddNewMemberDiscount = useCallback(async () => {
    if (!newTerm.trim() || !newDefinition.trim()) {
      setAddTermError('Both name and email are required.');
      return;
    }

    if (!newDefinition.includes('@') || newDefinition.split('@').length !== 2) {
      setAddTermError('Invalid email format.');
      return;
    }

    if (!newImageFile) {
      setAddTermError('Image is required.');
      return;
    }

    setAddTermError('');
    setIsAdding(true);

    try {
      if (!memberDiscountsCategoryId) {
        console.error('No category found for MEMBER DISCOUNTS. Cannot add new entry.');
        return;
      }

      if (!rows[0]?.author) {
        console.error('No author information available from existing rows. Cannot add new entry.');
        return;
      }

      const created = await postPartnerResource({
        category: memberDiscountsCategoryId,
        author: rows[0].author.id,
        title: newTerm,
        description: newDefinition,
        sub_description: newDefinition,
      });

      const createdImages = await patchPartnerResourceImages(Number(created.id), newImageFile, null);
      created.images = createdImages.images;

      setRows((prev) => [created, ...prev]);
      setRowCount((prev) => prev + 1);

      handleCloseAdd();
      setShowSaved(true);
    } catch (error) {
      console.error('Error adding new member discount:', error);
    } finally {
      setIsAdding(false);
    }
  }, [newTerm, newDefinition, newImageFile, memberDiscountsCategoryId, rows, handleCloseAdd]);

  const columns: GridColDef<PartnerResource>[] = useMemo(
    () => [
      {
        field: 'imageUrl',
        headerName: 'Image',
        width: 150,
        filterable: false,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        disableColumnMenu: true,
        renderCell: (params) => {
          const imgUrl =
            params.row.images?.find((img) => img.type === ResourceImageType.IMAGE)?.image;

          return imgUrl ? (
            <img
              src={imgUrl}
              alt={params.row.title}
              style={{ width: 100, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <Box
              sx={{
                width: 100,
                height: 100,
                backgroundColor: 'grey.300',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                No Image
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Name',
        filterOperators: stringContainsOnly,
        width: 260,
        valueGetter: (params) => params.row.title,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'description',
        headerName: 'Email',
        filterOperators: stringContainsOnly,
        width: 520,
        valueGetter: (params) => params.row.subDescription,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'updated_at',
        headerName: 'Updated At',
        filterOperators: stringContainsOnly,
        width: 180,
        valueGetter: (params) =>
          params.row.updatedAt ? moment(params.row.updatedAt).format('M/D/YYYY') : 'Unknown',
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 110,
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        cellClassName: 'actionsCell',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDelete(params.row);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleOpenDelete, handleOpenEdit],
  );

  const previewRows = useMemo(() => {
    if (!isEditOpen || !editingRow) return rows;

    const existingUrl = getResourceImageUrl(editingRow);
    const url = editImagePreviewUrl ?? existingUrl;

    const edited: PartnerResource = {
      ...editingRow,
      title: editTerm || editingRow.title,
      description: editDefinition || editingRow.description,
      subDescription: editDefinition || editingRow.subDescription,
      images: url
        ? [
          ...(editingRow.images ?? []).filter(
            (img) => img.type !== ResourceImageType.IMAGE,
          ),
          {
            id: -1,
            type: ResourceImageType.IMAGE,
            image: url,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            resource: Number(editingRow.id) || -1,
          },
        ]
        : (editingRow.images ?? []),
      updatedAt: new Date().toISOString(),
    };

    const rest = rows.filter((r) => r.id !== editingRow.id);
    return [edited, ...rest];
  }, [
    rows,
    isEditOpen,
    editingRow,
    editTerm,
    editDefinition,
    editImagePreviewUrl,
    getResourceImageUrl,
  ]);

  const addPreviewRows = useMemo(() => {
    if (!newKeyTermModalOpen) return rows;

    if (!newTerm.trim() && !newDefinition.trim() && !newImagePreviewUrl) return rows;

    const nowIso = new Date().toISOString();

    const draft: PartnerResource = {
      ...(rows[0] ?? ({} as PartnerResource)),
      id: -1,
      title: newTerm || 'Preview Name',
      description: newDefinition || 'Preview Email',
      subDescription: newDefinition || 'Preview Email',
      images: newImagePreviewUrl
        ? ([
          {
            id: -2,
            type: ResourceImageType.IMAGE,
            image: newImagePreviewUrl,
            createdAt: nowIso,
            updatedAt: nowIso,
            resource: -1,
          },
        ])
        : [],
      updatedAt: nowIso,
    };

    return [draft, ...rows];
  }, [rows, newKeyTermModalOpen, newTerm, newDefinition, newImagePreviewUrl]);

  return (
    <div>
      <Snackbar
        open={showSaved}
        autoHideDuration={4000}
        onClose={() => setShowSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSaved(false)}
          icon={<ThumbUp fontSize="inherit" />}
          severity="success"
          sx={{ width: '100%' }}
        >
          Member discount saved successfully
        </Alert>
      </Snackbar>

      <Card sx={{ padding: '2em' }}>
        <DataGridPro
          loading={loading}
          style={{ height: 520, width: '100%' }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id || ''}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onSortModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(model);
          }}
          onPageChange={(newPage: number) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newPageSize: number) =>
            setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }))
          }
          onFilterModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            const normalized = normalizeFilterModel(model);
            if (!sameModel(normalized, filterModelApplied)) {
              setFilterModelApplied(normalized);
            }
          }}
          rowsPerPageOptions={[25, 50, 100]}
          rowHeight={64}
          headerHeight={56}
          sx={{
            '& .MuiDataGrid-cell': { boxSizing: 'border-box' },
            '& .MuiDataGrid-cell--pinnedRight': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-cell.actionsCell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingTop: 0,
              paddingBottom: 0,
              gap: 0.5,
            },
            '& .MuiDataGrid-cell.actionsCell .MuiIconButton-root': { padding: 1 },
          }}
          components={{
            Toolbar: CustomToolbar,
            LoadingOverlay: () => (
              <GridOverlay>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <CircularProgress />
                </div>
              </GridOverlay>
            ),
          }}
          componentsProps={{
            toolbar: {
              openFilterModal: () => {},
              filterModelDraft,
              setFilterModelDraft,
              handleFetchVerificationData: () => {},
            },
          }}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
        />

        {/* Add Modal */}
        <StandardDialog
          title="Add Member Group Discount"
          subtitle="Create a new member group discount entry to be displayed in the Partner Resource Center."
          handleClose={handleCloseAdd}
          isOpen={newKeyTermModalOpen}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {newImagePreviewUrl ? (
                  <img
                    src={newImagePreviewUrl}
                    alt={newTerm || 'Discount image'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No Image
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" component="label" disabled={isAdding}>
                  Upload Image *
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewImageFile(e.target.files?.[0] ?? null)}
                  />
                </Button>

                {newImageFile && (
                  <Typography variant="caption" color="text.secondary">
                    Selected: {newImageFile.name}
                  </Typography>
                )}
              </Box>
            </Box>

            <TextField label="Name" value={newTerm} onChange={(e) => setNewTerm(e.target.value)} fullWidth disabled={isAdding} />
            <TextField
              label="Email"
              value={newDefinition}
              onChange={(e) => setNewDefinition(e.target.value)}
              fullWidth
              disabled={isAdding}
            />

            {addTermError && (
              <Alert severity="error" onClose={() => setAddTermError('')}>
                {addTermError}
              </Alert>
            )}
          </Box>

          <StandardDialogActions>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
            <Button onClick={handleCloseAdd} disabled={isAdding}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
             <Button
                variant="outlined"
                onClick={() => setShowAddPreview(true)}
                disabled={isAdding || !newTerm || !newDefinition || !newImagePreviewUrl}
                sx={{ mr: 1 }}
                >
                Live Preview
              </Button>
            <AsyncSaveButton variant="contained" onClick={handleAddNewMemberDiscount} isSaving={isAdding} label="Add" />
            </Box>
          </StandardDialogActions>
        </StandardDialog>

        {/* Edit Modal */}
        <StandardDialog
          isOpen={isEditOpen}
          title="Edit Member Group Discount"
          subtitle="Update the member info and optionally replace the image."
          handleClose={handleCloseEdit}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {(() => {
                  const previewUrl = editImagePreviewUrl ?? getResourceImageUrl(editingRow);
                  return previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={editingRow?.title || 'Discount image'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No Image
                    </Typography>
                  );
                })()}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" component="label" disabled={isSaving}>
                  Replace Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditImageFile(e.target.files?.[0] ?? null)}
                  />
                </Button>

                {editImageFile && (
                  <Typography variant="caption" color="text.secondary">
                    New image: {editImageFile.name}
                  </Typography>
                )}
              </Box>
            </Box>

            <TextField
              label="Name"
              value={editTerm}
              onChange={(e) => setEditTerm(e.target.value)}
              fullWidth
              disabled={isSaving}
            />

            <TextField
              label="Email"
              value={editDefinition}
              onChange={(e) => setEditDefinition(e.target.value)}
              fullWidth
              disabled={isSaving}
            />

            {editKeyTermError && (
              <Alert severity="error" onClose={() => setEditKeyTermError('')}>
                {editKeyTermError}
              </Alert>
            )}
          </Box>

          <StandardDialogActions>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <Button onClick={handleCloseAdd} disabled={isSaving}>
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    onClick={() => setShowPRCPreview(true)}
                    disabled={isSaving}
                    sx={{ mr: 1 }}
                    >
                    Live Preview
                </Button>
                <AsyncSaveButton variant='contained' onClick={handleSaveEdit} isSaving={isSaving} label="Save" />
            </Box>
          </StandardDialogActions>
        </StandardDialog>

        {/* Delete Confirmation Modal */}
        <StandardDialog isOpen={isDeleteOpen} title="Delete Member Discount?" handleClose={handleCloseDelete}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              Are you sure you want to delete <strong>{deletingRow?.title || 'this entry'}</strong>? This action cannot be undone.
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={handleCloseDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <AsyncSaveButton onClick={handleDeleteConfirmed} isSaving={isDeleting} label="Delete" />
            </Box>
          </Box>
        </StandardDialog>
      </Card>
      <PartnerResourcesModal
        partnerResources={showAddPreview ? addPreviewRows : (previewRows || [])}
        showResourceModal={showAddPreview || showPRCPreview}
        preventUrlChange
        previewCategory={RESOURCE_CATEGORY_TITLES.MEMBER_DISCOUNTS}
        handleCloseResourceModal={() => {
          setShowAddPreview(false);
          setShowPRCPreview(false);
        }}
        fetchResources={() => {
          if (memberDiscountsCategoryId) fetchMemberDiscounts(memberDiscountsCategoryId);
        }}
      />
    </div>
  );
};

export default MemberGroupDiscounts;